import Button from '@shared/components/Button/Button';
import {
  ClientEvent,
  EventType,
  KnownMembership,
  MatrixError,
  RestrictedAllowType,
  Room,
  RoomMember,
  RoomType,
  SyncState,
} from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as UserPlus } from 'assets/icons/UserPlus.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { MatrixMembershipChangeReasons } from 'hooks/useManageMatrixRoomUsers';
import { useDeferInviteSession } from 'modules/Notifications/DeferInviteSessionProvider';
import { findUserInviteMemberEvent } from 'modules/Notifications/deferredInviteSnapshot';
import { inviteFlushDebug } from 'modules/Notifications/inviteFlushDebug';
import { dismissInviteNotification } from 'modules/Notifications/inviteNotificationDismiss';
import { getMessengerRoomInviteNotificationId } from 'modules/Notifications/inviteNotificationIds';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import { shouldProcessInviteNotifications } from 'modules/Notifications/shouldProcessInviteNotifications';
import useDeferredInviteGate from 'modules/Notifications/useDeferredInviteGate';
import useDeferredInviteSnapshot from 'modules/Notifications/useDeferredInviteSnapshot';
import useInviteDeferFlush from 'modules/Notifications/useInviteDeferFlush';
import logger from 'utils/logging/faro';
import { isInstantCallRoom, waitForMembership } from 'utils/matrixUtils';
import { isWorkspaceRoom } from 'utils/roomHierarchyUtils';
import { syncLinkedScheduledMeetingInvites } from 'utils/syncLinkedScheduledMeetingInvites';

const RECENT_INVITE_THRESHOLD = 1000 * 10;

// BUG-001 — a moderator-promotion invite is a persistent meeting space (not instant-call, not
// workspace) whose own stripped invite member event carries reason 'moderator'. This is the only
// pre-join signal available: the eu.meedio.room.managers map is absent from stripped invite state.
const isModeratorSpaceInvite = (room: Room, myUserId: string): boolean => {
  if (room.getType() !== RoomType.Space) return false;
  if (isInstantCallRoom(room) || isWorkspaceRoom(room)) return false;
  return findUserInviteMemberEvent(room, myUserId)?.getContent().reason === MatrixMembershipChangeReasons.MODERATOR;
};

const useMessengerRoomInviteNotifications = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const deferSession = useDeferInviteSession();
  const { addNotification, removeNotification, editNotification, markAsRead, isNotificationRead } = useNotifications();
  const { pushToast, pushNotificationToast, removeToast, isToastPresent, editNotificationToast } = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const shownInvitesRef = useRef<Set<string>>(new Set());
  const combinedChatIdsRef = useRef<Map<string, string>>(new Map());
  // Chat rooms whose combined invite was declined before the chat invite arrived.
  // processInvites auto-leaves and discards them when the invite syncs.
  const pendingChatDeclineIdsRef = useRef<Set<string>>(new Set());
  const flushPendingRef = useRef(false);

  const clearDeferredUi = useCallback(() => {
    [...shownInvitesRef.current].forEach((roomId) => {
      const notificationId = getMessengerRoomInviteNotificationId(roomId);
      removeNotification(notificationId);
      removeToast(notificationId);
      shownInvitesRef.current.delete(roomId);
    });
  }, [removeNotification, removeToast]);

  const { isDeferred, isDeferredRef } = useDeferredInviteGate(clearDeferredUi);
  const deferredSnapshot = useDeferredInviteSnapshot(
    isDeferred,
    (member: RoomMember) => {
      if (member.membership !== KnownMembership.Invite) return false;
      const room = matrixClient.getRoom(member.roomId);
      if (!room) return false;
      if (room.getType() !== RoomType.Space || isInstantCallRoom(room)) return true;
      // BUG-001 — also track moderator-promotion space invites so they flush on return to the shell.
      const myUserId = matrixClient.getUserId();
      return !!myUserId && isModeratorSpaceInvite(room, myUserId);
    },
    (member: RoomMember) => {
      if (member.membership !== KnownMembership.Join) return false;
      if (deferSession.isRoomInBaseline(member.roomId)) return false;
      const room = matrixClient.getRoom(member.roomId);
      if (!room) return false;
      if (room.getType() !== RoomType.Space || isInstantCallRoom(room)) return true;
      // BUG-001 — a moderator-space invite that was joined during the defer window still flushes.
      const myUserId = matrixClient.getUserId();
      return !!myUserId && isModeratorSpaceInvite(room, myUserId);
    },
    (roomId) => deferSession.isRoomInBaseline(roomId)
  );

  const isMessengerInviteRoom = (room: Room, myUserId: string): boolean => {
    if (room.getMyMembership() !== KnownMembership.Invite) return false;
    // BUG-001 — a persistent meeting space normally excluded here surfaces when the invite reason is
    // 'moderator' (promotion invite); instant-call spaces still pass as before.
    if (room.getType() === RoomType.Space && !isInstantCallRoom(room)) {
      return isModeratorSpaceInvite(room, myUserId);
    }
    return true;
  };

  const processInvites = useCallback(() => {
    const myUserId = matrixClient.getUserId();
    if (!myUserId) return;

    const currentInvites = shownInvitesRef.current;

    const removeMessengerInviteUi = (roomId: string) => {
      const notificationId = getMessengerRoomInviteNotificationId(roomId);
      removeNotification(notificationId);
      removeToast(notificationId);
      currentInvites.delete(roomId);
      combinedChatIdsRef.current.delete(roomId);
      pendingChatDeclineIdsRef.current.delete(roomId);
    };

    if (isDeferredRef.current) {
      [...currentInvites].forEach(removeMessengerInviteUi);
      return;
    }

    const isFlush = flushPendingRef.current && deferSession.deferStartedAt !== null;
    flushPendingRef.current = false;

    const flushedSnapshots = isFlush ? deferredSnapshot.drain() : [];
    const eligibleSnapshots = flushedSnapshots.filter((snapshot) =>
      deferSession.isEligibleForFlush({
        notificationId: getMessengerRoomInviteNotificationId(snapshot.roomId),
        inviteTimestamp: snapshot.inviteTimestamp,
        trackedAt: snapshot.trackedAt,
        isNotificationRead,
      })
    );
    const snapshotByRoomId = new Map(eligibleSnapshots.map((snapshot) => [snapshot.roomId, snapshot]));

    const nextInviteRoomIds = new Set<string>(
      matrixClient
        .getRooms()
        .filter((room) => {
          if (isFlush && deferSession.isRoomInBaseline(room.roomId)) return false;
          return isMessengerInviteRoom(room, myUserId);
        })
        .map((room) => room.roomId)
    );

    // Eligible snapshots from the store cover both membership_invite and
    // membership_join_during_defer rooms (tracked by the listener + seed scan).
    // No secondary full-room scan here — that would bypass the store and risk
    // surfacing the call room itself or rooms outside this hook's domain.
    eligibleSnapshots.forEach((snapshot) => nextInviteRoomIds.add(snapshot.roomId));

    currentInvites.forEach((roomId) => {
      if (!nextInviteRoomIds.has(roomId)) removeMessengerInviteUi(roomId);
    });

    if (isFlush) {
      inviteFlushDebug('MSG-FLUSH-DEBUG', {
        isFlush,
        flushedCount: flushedSnapshots.length,
        eligibleCount: eligibleSnapshots.length,
        nextInviteRoomIds: [...nextInviteRoomIds],
        sessionActive: deferSession.isActive,
        deferStartedAt: deferSession.deferStartedAt,
      });
    }

    // Pre-compute which chat rooms are already covered by a meeting-space invite in this batch.
    // This prevents a brief separate chat toast when both invites arrive in the same sync batch
    // and the chat room happens to be iterated before the meeting space.
    const chatRoomsCoveredInBatch = new Set<string>();
    for (const roomId of nextInviteRoomIds) {
      const room = matrixClient.getRoom(roomId);
      if (!room || !isInstantCallRoom(room)) continue;
      const joinRulesContent = room.currentState
        ?.getStateEvents(EventType.RoomJoinRules, '')
        ?.getContent<{ join_rule: string; allow?: Array<{ type: string; room_id: string }> }>();
      const linkedId =
        joinRulesContent?.join_rule === 'restricted'
          ? joinRulesContent.allow?.find((a) => a.type === RestrictedAllowType.RoomMembership)?.room_id ?? null
          : null;
      if (linkedId) chatRoomsCoveredInBatch.add(linkedId);
    }

    for (const roomId of nextInviteRoomIds) {
      // Auto-decline a chat invite that arrived after the user already declined the combined toast.
      if (pendingChatDeclineIdsRef.current.has(roomId)) {
        pendingChatDeclineIdsRef.current.delete(roomId);
        matrixClient.leave(roomId).catch((error) => {
          logger.error(`Failed to auto-decline pending linked chat invite for room ${roomId}`, { error });
        });
        continue;
      }

      // #8237: suppress any chat room that is covered by a meeting-space invite arriving in
      // this same sync batch (pre-suppression) OR by a meeting toast already on screen
      // (combinedChatIdsRef deduplication across future syncs).
      const alreadyCoveredByMeetingToast =
        chatRoomsCoveredInBatch.has(roomId) || [...combinedChatIdsRef.current.values()].includes(roomId);
      if (alreadyCoveredByMeetingToast) {
        if (currentInvites.has(roomId)) removeMessengerInviteUi(roomId);
        continue;
      }

      const room = matrixClient.getRoom(roomId);
      if (!room) continue;

      const membership = room.getMyMembership();
      if (membership === KnownMembership.Leave || membership === KnownMembership.Ban) continue;

      const wasDeferredInvite = snapshotByRoomId.has(roomId);
      const isAlreadyJoined = membership === KnownMembership.Join && wasDeferredInvite;
      if (!isAlreadyJoined && membership !== KnownMembership.Invite) continue;

      const inviteEvent = findUserInviteMemberEvent(room, myUserId);

      const notificationId = getMessengerRoomInviteNotificationId(roomId);
      const snapshot = snapshotByRoomId.get(roomId);
      const inviterId = inviteEvent?.getSender() ?? snapshot?.inviterId;
      const inviteTimestamp = inviteEvent?.getTs() ?? snapshot?.inviteTimestamp;
      const notificationTimestamp =
        typeof inviteTimestamp === 'number' && inviteTimestamp > 0 ? inviteTimestamp : Date.now();
      const isRecentInvite =
        !!inviteTimestamp && inviteTimestamp > 0 && Date.now() - inviteTimestamp < RECENT_INVITE_THRESHOLD;

      const inviterDisplayName =
        matrixClient.getUser(inviterId || '')?.displayName || inviterId?.split(':')[0]?.replace('@', '');

      if (isAlreadyJoined) {
        if (isFlush) {
          inviteFlushDebug('MSG-FLUSH-DEBUG', { step: 'add-acknowledgment', roomId, roomName: room.name, membership });
        }

        const handleViewChat = () => {
          navigate(`/messenger/${roomId}`);
          dismissInviteNotification({
            kind: 'close',
            notificationId,
            roomId,
            markAsRead,
            removeNotification,
            removeToast,
          });
        };

        const handleLeave = () => {
          dismissInviteNotification({
            kind: 'decline',
            notificationId,
            roomId,
            markAsRead,
            removeNotification,
            removeToast,
            matrixClient,
          });
        };

        const acknowledgmentTitle = (
          <Trans
            i18nKey="messenger.room_invite_added_during_call"
            components={{ bold: <span className="font-medium" /> }}
            values={{ inviterName: inviterDisplayName, roomName: room.name }}
          />
        );

        addNotification({
          id: notificationId,
          icon: UserPlus,
          variant: 'primary',
          title: acknowledgmentTitle,
          content: (
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="tertiary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLeave();
                }}
              >
                <Trans i18nKey="leave" />
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewChat();
                }}
              >
                <Trans i18nKey="view" />
              </Button>
            </div>
          ),
          isRead: false,
          timestamp: notificationTimestamp,
          onClick: handleViewChat,
        });

        if (isFlush) {
          pushNotificationToast({
            id: notificationId,
            icon: <UserPlus className="h-5 w-5 stroke-primary-70 stroke-1.5" />,
            title: acknowledgmentTitle,
            onPrimaryAction: handleViewChat,
            primaryActionText: t('view'),
            secondaryActionText: t('leave'),
            onSecondaryAction: handleLeave,
            onClose: () => {
              dismissInviteNotification({
                kind: 'close',
                notificationId,
                roomId,
                markAsRead,
                removeNotification,
                removeToast,
              });
            },
          });
        }

        currentInvites.add(roomId);
        continue;
      }

      const isInstantCall = isInstantCallRoom(room);
      // BUG-001 — moderator-promotion invite: routes to the Rooms tab like an instant call, but has
      // no linked chat, so the combined-chat machinery below (gated on isInstantCall) stays inert.
      const isModeratorInvite =
        !isInstantCall && inviteEvent?.getContent().reason === MatrixMembershipChangeReasons.MODERATOR;

      // #8237: for an invited instant-call space, the linked group chat is carried in the
      // restricted join rule's `allow` list (written by `applyMessengerRestrictedJoin`). This is a
      // standard stripped-state event, so it is readable synchronously here.
      let linkedChatRoomId: string | null = null;
      if (isInstantCall) {
        const joinRulesEvent = room.currentState?.getStateEvents(EventType.RoomJoinRules, '');
        const joinRulesContent = joinRulesEvent?.getContent<{
          join_rule: string;
          allow?: Array<{ type: string; room_id: string }>;
        }>();
        linkedChatRoomId =
          joinRulesContent?.join_rule === 'restricted'
            ? joinRulesContent.allow?.find((a) => a.type === RestrictedAllowType.RoomMembership)?.room_id ?? null
            : null;
      }
      // #8237: "eager combined" — show combined toast as soon as the meeting-space invite arrives
      // with restricted join_rules, even if the chat invite hasn't arrived yet.  Only skip combined
      // when the user is already a Join member (gate passes, no invite needed).
      const chatMembership = linkedChatRoomId ? matrixClient.getRoom(linkedChatRoomId)?.getMyMembership() : undefined;
      const isCombinedInvite = !!linkedChatRoomId && chatMembership !== KnownMembership.Join;

      const inviteTitle = isCombinedInvite ? (
        <Trans
          i18nKey="messenger.room_invite_received_with_chat"
          components={{ bold: <span className="font-medium" /> }}
          values={{ inviterName: inviterDisplayName }}
        />
      ) : (
        <Trans
          i18nKey="messenger.room_invite_received"
          components={{ bold: <span className="font-medium" /> }}
          values={{ inviterName: inviterDisplayName, roomName: room.name }}
        />
      );

      const joinDestination = isInstantCall || isModeratorInvite ? `/rooms/${roomId}` : `/messenger/${roomId}`;

      const handleJoin = async () => {
        // #8237: join the linked chat first so `RoomIdentityProvider.isBlockedByMessenger` passes
        // by the time we navigate to the meeting space (avoids the /404 gate).
        if (isCombinedInvite && linkedChatRoomId) {
          const userId = matrixClient.getUserId();
          // If the chat invite hasn't arrived yet (eager combined: we detected the linked chat
          // from restricted join_rules before the invite synced), wait for it before joining.
          if (chatMembership !== KnownMembership.Invite && chatMembership !== KnownMembership.Join && userId) {
            await waitForMembership(matrixClient, linkedChatRoomId, userId, KnownMembership.Invite);
          }
          try {
            await matrixClient.joinRoom(linkedChatRoomId);
            if (userId) {
              await waitForMembership(matrixClient, linkedChatRoomId, userId, KnownMembership.Join);
            }
          } catch (error) {
            if (!(error instanceof MatrixError && error.errcode === 'M_ALREADY_MEMBER')) {
              pushToast({ variant: 'error', title: t('errors.something_went_wrong') });
              return;
            }
          }
        }

        try {
          await matrixClient.joinRoom(roomId);
          const userId = matrixClient.getUserId();
          if (userId) {
            await waitForMembership(matrixClient, roomId, userId, KnownMembership.Join);
            await syncLinkedScheduledMeetingInvites(matrixClient, roomId, [userId]);
          }
        } catch (error) {
          if (!(error instanceof MatrixError && error.errcode === 'M_ALREADY_MEMBER')) {
            pushToast({ variant: 'error', title: t('errors.something_went_wrong') });
            return;
          }
        }

        navigate(joinDestination);

        editNotification(notificationId, {
          content: <p>{t('messenger.room_invite_joined', { roomName: room.name })}</p>,
          onClick: () => navigate(joinDestination),
          icon: UserPlus,
          variant: 'primary',
          title: t('messenger.room_invite_joined', { roomName: room.name }),
          isRead: false,
          timestamp: notificationTimestamp,
        });
        removeToast(notificationId);
      };

      const handleDecline = () => {
        // #8237: a combined invite also declines the linked chat.  Re-read membership at decline
        // time since it may have changed since processInvites ran.  Never force-leave a room the
        // user has already joined (KnownMembership.Join).
        if (isCombinedInvite && linkedChatRoomId) {
          combinedChatIdsRef.current.delete(roomId);
          const chatMembershipNow = matrixClient.getRoom(linkedChatRoomId)?.getMyMembership();
          if (chatMembershipNow === KnownMembership.Invite) {
            matrixClient.leave(linkedChatRoomId).catch((error) => {
              logger.error(`Failed to decline linked chat invite for room ${linkedChatRoomId}`, { error });
            });
          } else if (chatMembershipNow !== KnownMembership.Join) {
            // Chat invite hasn't arrived yet — schedule auto-decline for when it does.
            pendingChatDeclineIdsRef.current.add(linkedChatRoomId);
          }
        }

        dismissInviteNotification({
          kind: 'decline',
          notificationId,
          roomId,
          markAsRead,
          removeNotification,
          removeToast,
          matrixClient,
        });
      };

      // #8040: invite already tracked — refresh the toast's localized action texts on language
      // change (the panel <Trans> title/buttons are already reactive), then skip re-adding.
      // #8237: if a previously single toast is now detectable as combined (the restricted join rule
      // arrived on a later sync), upgrade it via remove-and-re-add instead of refreshing.
      if (currentInvites.has(roomId)) {
        const shouldUpgradeToCombined =
          !combinedChatIdsRef.current.has(roomId) && isCombinedInvite && !!linkedChatRoomId;
        if (shouldUpgradeToCombined) {
          removeNotification(notificationId);
          removeToast(notificationId);
          currentInvites.delete(roomId);
          // Also evict any separately-shown chat toast before the upgrade falls through to add.
          if (linkedChatRoomId && currentInvites.has(linkedChatRoomId)) {
            removeMessengerInviteUi(linkedChatRoomId);
          }
        } else {
          if (isFlush) {
            inviteFlushDebug('MSG-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'already in currentInvites' });
          }
          if (isToastPresent(notificationId)) {
            editNotificationToast(notificationId, {
              id: notificationId,
              icon: <UserPlus className="h-5 w-5 stroke-primary-70 stroke-1.5" />,
              title: inviteTitle,
              onPrimaryAction: handleJoin,
              primaryActionText: t('join'),
              secondaryActionText: t('decline'),
              onSecondaryAction: handleDecline,
            });
          }
          continue;
        }
      }

      if (isFlush) {
        inviteFlushDebug('MSG-FLUSH-DEBUG', { step: 'add-invite', roomId, isRecentInvite, roomName: room.name });
      }

      if (isDeferredRef.current) continue;

      addNotification({
        id: notificationId,
        icon: UserPlus,
        variant: 'primary',
        title: inviteTitle,
        content: (
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="tertiary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDecline();
              }}
            >
              <Trans i18nKey="decline" />
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleJoin();
              }}
            >
              <Trans i18nKey="join" />
            </Button>
          </div>
        ),
        isRead: false,
        timestamp: notificationTimestamp,
        onClick: () => navigate(joinDestination),
      });

      logger.info(`Added messenger room invite notification for room ${roomId} from ${inviterId || 'unknown'}`);

      if (isRecentInvite || isFlush) {
        if (isDeferredRef.current) continue;

        pushNotificationToast({
          id: notificationId,
          icon: <UserPlus className="h-5 w-5 stroke-primary-70 stroke-1.5" />,
          title: inviteTitle,
          onPrimaryAction: handleJoin,
          primaryActionText: t('join'),
          secondaryActionText: t('decline'),
          onSecondaryAction: handleDecline,
          onClose: () => {
            dismissInviteNotification({
              kind: 'close',
              notificationId,
              roomId,
              markAsRead,
              removeNotification,
              removeToast,
            });
          },
        });

        logger.info(`Displayed messenger room invite toast for room ${roomId} from ${inviterId || 'unknown'}`);
      }

      if (isCombinedInvite && linkedChatRoomId) {
        combinedChatIdsRef.current.set(roomId, linkedChatRoomId);
        // #8237: retroactively suppress any separately-shown chat invite notification/toast.
        // This is a belt-and-suspenders fallback for the edge case where the chat invite and
        // meeting-space invite arrive in the same sync batch and the chat room is iterated first.
        if (currentInvites.has(linkedChatRoomId)) {
          removeMessengerInviteUi(linkedChatRoomId);
        }
      }

      currentInvites.add(roomId);
    }
  }, [
    matrixClient,
    deferSession,
    addNotification,
    removeNotification,
    editNotification,
    markAsRead,
    isNotificationRead,
    pushToast,
    pushNotificationToast,
    removeToast,
    isToastPresent,
    editNotificationToast,
    navigate,
    t,
    deferredSnapshot,
    isDeferredRef,
  ]);

  useInviteDeferFlush(isDeferred, () => {
    flushPendingRef.current = true;
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const processInvitesIfReady = () => {
      if (!shouldProcessInviteNotifications(matrixClient)) return;
      processInvites();
    };

    // Debounce subsequent sync/room events by 2 s so that a meeting-space invite and its paired
    // chat invite settle into the same processInvites run even when the server delivers them in
    // consecutive sync batches, eliminating the brief chat-only toast that appears before upgrade.
    const scheduleProcessInvites = () => {
      if (debounceTimerRef.current !== null) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(processInvitesIfReady, 2000);
    };

    const handleSync = (state: SyncState) => {
      if (state === SyncState.Syncing) scheduleProcessInvites();
    };

    matrixClient.on(ClientEvent.Sync, handleSync);
    matrixClient.on(ClientEvent.Room, scheduleProcessInvites);

    processInvitesIfReady();

    return () => {
      matrixClient.off(ClientEvent.Sync, handleSync);
      matrixClient.off(ClientEvent.Room, scheduleProcessInvites);
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [matrixClient, processInvites, pathname]);
};

export default useMessengerRoomInviteNotifications;
